import React, { useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Plus, X, BarChart3, CalendarDays, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const emptyOpt = () => ({ label: '', date: '', time: '' });

const OptionRow = ({ kind, opt, index, canRemove, onChange, onRemove }) => {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-2" data-testid={`poll-form-option-${index}`}>
      {kind === 'date' ? (
        <>
          <Input type="date" value={opt.date} onChange={(e) => onChange({ ...opt, date: e.target.value })} data-testid={`poll-form-option-date-${index}`} className="flex-1 min-w-0" />
          <Input type="time" value={opt.time} onChange={(e) => onChange({ ...opt, time: e.target.value })} data-testid={`poll-form-option-time-${index}`} className="w-28 shrink-0" />
        </>
      ) : (
        <Input value={opt.label} placeholder={t('polls.optionPlaceholder', { n: index + 1 })} onChange={(e) => onChange({ ...opt, label: e.target.value })} data-testid={`poll-form-option-label-${index}`} className="flex-1 min-w-0" />
      )}
      <button type="button" onClick={onRemove} disabled={!canRemove} data-testid={`poll-form-option-remove-${index}`} className="shrink-0 w-9 h-9 flex items-center justify-center rounded-lg text-zinc-400 hover:text-[#D93025] hover:bg-[#FEE2E2] disabled:opacity-30 disabled:hover:bg-transparent">
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

const KindToggle = ({ kind, setKind }) => {
  const { t } = useTranslation();
  const item = (id, Icon, label) => (
    <button type="button" onClick={() => setKind(id)} data-testid={`poll-form-kind-${id}`}
      className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-sm font-semibold transition-colors ${kind === id ? 'bg-white shadow text-[#D93025]' : 'text-zinc-500 hover:text-zinc-800'}`}>
      <Icon className="w-4 h-4" />{label}
    </button>
  );
  return (
    <div className="flex gap-1 p-1 bg-zinc-100 rounded-xl">
      {item('options', BarChart3, t('polls.kindOptions'))}
      {item('date', CalendarDays, t('polls.kindDate'))}
    </div>
  );
};

export const CreatePollDialog = ({ open, onOpenChange, chatTarget, onCreated }) => {
  const { t } = useTranslation();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [kind, setKind] = useState('options');
  const [options, setOptions] = useState([emptyOpt(), emptyOpt()]);
  const [multiple, setMultiple] = useState(false);
  const [anonymous, setAnonymous] = useState(false);
  const [deadline, setDeadline] = useState('');
  const [saving, setSaving] = useState(false);

  const reset = () => {
    setTitle(''); setDescription(''); setKind('options'); setOptions([emptyOpt(), emptyOpt()]);
    setMultiple(false); setAnonymous(false); setDeadline('');
  };

  const changeKind = (k) => { setKind(k); if (k === 'date') setMultiple(true); };

  const submit = async () => {
    const valid = options.filter((o) => (kind === 'date' ? o.date : o.label.trim()));
    if (!title.trim()) { toast.error(t('polls.titleRequired')); return; }
    if (valid.length < 2) { toast.error(t('polls.optionsRequired')); return; }
    setSaving(true);
    try {
      const res = await axios.post(`${API_URL}/polls`, {
        title, description, kind, multiple, anonymous,
        options: valid.map((o) => (kind === 'date' ? { date: o.date, time: o.time || null } : { label: o.label })),
        deadline: deadline ? new Date(deadline).toISOString() : null,
        chat: chatTarget || null,
      });
      toast.success(t('polls.createdToast'));
      reset();
      onOpenChange(false);
      onCreated?.(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || t('polls.error'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="poll-create-dialog">
        <DialogHeader>
          <DialogTitle>{t('polls.newPoll')}</DialogTitle>
          <DialogDescription>{chatTarget ? t('polls.postToChatHint') : t('polls.newPollHint')}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <KindToggle kind={kind} setKind={changeKind} />
          <div>
            <Label htmlFor="poll-title">{t('polls.question')}</Label>
            <Input id="poll-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder={kind === 'date' ? t('polls.questionDatePlaceholder') : t('polls.questionPlaceholder')} data-testid="poll-form-title" />
          </div>
          <div>
            <Label htmlFor="poll-desc">{t('polls.description')}</Label>
            <Textarea id="poll-desc" rows={2} value={description} onChange={(e) => setDescription(e.target.value)} data-testid="poll-form-description" />
          </div>
          <div className="space-y-2">
            <Label>{kind === 'date' ? t('polls.dateOptions') : t('polls.options')}</Label>
            {options.map((o, i) => (
              <OptionRow key={i} kind={kind} opt={o} index={i} canRemove={options.length > 2}
                onChange={(v) => setOptions((os) => os.map((x, j) => (j === i ? v : x)))}
                onRemove={() => setOptions((os) => os.filter((_, j) => j !== i))} />
            ))}
            <button type="button" onClick={() => setOptions((os) => [...os, emptyOpt()])} data-testid="poll-form-add-option" className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#D93025] hover:underline">
              <Plus className="w-4 h-4" />{t('polls.addOption')}
            </button>
          </div>
          <div className="flex items-center justify-between gap-3">
            <Label htmlFor="poll-multiple">{t('polls.allowMultiple')}</Label>
            <Switch id="poll-multiple" checked={multiple} onCheckedChange={setMultiple} data-testid="poll-form-multiple" />
          </div>
          <div className="flex items-center justify-between gap-3">
            <Label htmlFor="poll-anon">{t('polls.anonymousVotes')}</Label>
            <Switch id="poll-anon" checked={anonymous} onCheckedChange={setAnonymous} data-testid="poll-form-anonymous" />
          </div>
          <div>
            <Label htmlFor="poll-deadline">{t('polls.deadline')}</Label>
            <Input id="poll-deadline" type="datetime-local" value={deadline} onChange={(e) => setDeadline(e.target.value)} data-testid="poll-form-deadline" />
          </div>
          <Button onClick={submit} disabled={saving} data-testid="poll-form-submit" className="w-full bg-[#D93025] hover:bg-[#B91C1C] text-white">
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}{t('polls.create')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CreatePollDialog;
